import { shallowRef, ref, computed, triggerRef } from 'vue'
import {
  onAuthStateChanged,
  signInWithPopup,
  signInAnonymously,
  signInWithCredential,
  linkWithPopup,
  GoogleAuthProvider,
  signOut,
  type User,
} from 'firebase/auth'
import { doc, setDoc, getDoc, getDocs, collection, Timestamp } from 'firebase/firestore'
import * as Sentry from '@sentry/vue'
import { auth, db } from '@/firebase'

// shallowRef: Firebase mutates the User object in place (e.g. after linking),
// so we call triggerRef() manually when that happens.
const currentUser = shallowRef<User | null>(null)
const isLoading = ref(true)
const loginPending = ref(false)

/** True for guest sessions created by signInAnonymously. */
const isAnonymous = computed(() => !!currentUser.value?.isAnonymous)
/** True only for users signed in with a real provider (Google). */
const isRegistered = computed(() => !!currentUser.value && !currentUser.value.isAnonymous)

let initialized = false

async function ensureUserDoc(user: User) {
  const userRef = doc(db, 'users', user.uid)
  const userSnap = await getDoc(userRef)
  if (!userSnap.exists()) {
    await setDoc(userRef, {
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      isAnonymous: user.isAnonymous,
      plan: 'free',
      createdAt: new Date(),
    })
  } else if (!user.isAnonymous && userSnap.data().isAnonymous) {
    // Guest account was just upgraded to Google: fill in the profile
    await setDoc(
      userRef,
      {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        isAnonymous: false,
        upgradedAt: new Date(),
      },
      { merge: true }
    )
  }
}

/**
 * Copies a guest's file docs to an existing Google account.
 * Storage objects stay under the guest uid; their download URLs keep working.
 */
async function migrateGuestFiles(fromUid: string, toUid: string, guestFiles: Awaited<ReturnType<typeof getDocs>>) {
  await Promise.all(
    guestFiles.docs.map((d) =>
      setDoc(doc(db, 'users', toUid, 'files', d.id), {
        ...(d.data() as Record<string, unknown>),
        migratedFromUid: fromUid,
        updatedAt: Timestamp.now(),
      })
    )
  )
}

export function useAuth() {
  if (!initialized) {
    initialized = true
    onAuthStateChanged(auth, async (user) => {
      currentUser.value = user
      if (user) {
        Sentry.setUser({ id: user.uid, email: user.email ?? undefined })
        try {
          await ensureUserDoc(user)
        } catch (e) {
          console.error('Could not create user doc:', e)
        }
      } else {
        Sentry.setUser(null)
      }
      isLoading.value = false
    })
  }

  /** Returns a session, creating a guest one if nobody is signed in. */
  async function ensureSession(): Promise<User> {
    if (auth.currentUser) return auth.currentUser
    const cred = await signInAnonymously(auth)
    currentUser.value = cred.user
    await ensureUserDoc(cred.user)
    return cred.user
  }

  async function loginWithGoogle() {
    if (loginPending.value) return
    loginPending.value = true
    const provider = new GoogleAuthProvider()
    try {
      const user = auth.currentUser
      if (!user?.isAnonymous) {
        return await signInWithPopup(auth, provider)
      }

      // Guest → Google: link so the uid (and all uploaded files) are kept
      try {
        const result = await linkWithPopup(user, provider)
        await ensureUserDoc(result.user)
        triggerRef(currentUser)
        return result
      } catch (e: any) {
        if (e?.code !== 'auth/credential-already-in-use') throw e

        // That Google account already exists: sign into it and bring the guest files along
        const credential = GoogleAuthProvider.credentialFromError(e)
        if (!credential) throw e
        const guestUid = user.uid
        const guestFiles = await getDocs(collection(db, 'users', guestUid, 'files'))
        const result = await signInWithCredential(auth, credential)
        await migrateGuestFiles(guestUid, result.user.uid, guestFiles)
        return result
      }
    } finally {
      loginPending.value = false
    }
  }

  async function logout() {
    return signOut(auth)
  }

  return {
    currentUser,
    isLoading,
    isAnonymous,
    isRegistered,
    loginPending,
    ensureSession,
    loginWithGoogle,
    logout,
  }
}
